import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useToast } from "@circle-react-uikit/ToastV2";
import { useRevealingMessageStore } from "../stores/useRevealingMessageStore";
import type { ConversationChannel } from "../types/ConversationChannel";
import type { HelpWidgetConversation } from "../types/help_widget_conversation";
import type { HelpWidgetMessage } from "../types/help_widget_message";
import { useClickButton } from "./useClickButton";
import { useCreateChat } from "./useCreateChat";
import { useCreateChatMessage } from "./useCreateChatMessage";
import { useUpdateConversation } from "./useUpdateConversation";

interface UseConversationMutationsProps {
  channel: ConversationChannel | null;
  clearVirtualMessages: () => void;
  conversationId: string;
  setAttachments: (value: []) => void;
  setIsBotProcessing: (value: boolean) => void;
}

export const useConversationMutations = ({
  channel,
  clearVirtualMessages,
  conversationId,
  setAttachments,
  setIsBotProcessing,
}: UseConversationMutationsProps) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const toastr = useToast();
  const { addMessage } = useRevealingMessageStore();

  const { mutate: createChatMessage } = useCreateChatMessage({
    onSuccess: (message: HelpWidgetMessage) => {
      if (channel === "circle_bot") {
        setIsBotProcessing(true);
      }
      setAttachments([]);
      void queryClient.invalidateQueries(["attachments", conversationId]);
      addMessage(message);
    },
    onError: () => {},
  });

  const { mutate: createChat } = useCreateChat({
    onSuccess: (conversation: HelpWidgetConversation) => {
      const conversationId = String(conversation.id);
      queryClient.setQueryData(
        ["help-widget", "conversations", conversationId],
        conversation,
      );
      clearVirtualMessages();
      history.push(`/conversations/${conversationId}`);
    },
    onError: () => {},
  });

  const { mutate: clickButton } = useClickButton({
    onSuccess: () => {},
    onError: () => {},
  });

  const { mutate: updateConversation } = useUpdateConversation({
    onSuccess: () => {
      void queryClient.invalidateQueries([
        "help-widget",
        "conversations",
        conversationId,
      ]);
    },
    onError: () => {
      toastr.error(t("request_failure_message"));
    },
  });

  return {
    createChatMessage,
    createChat,
    clickButton,
    updateConversation,
  };
};
